/**
 * PaymentSection — the customer-facing "Pay now" surface for a service request.
 *
 * Rendered in the provider workspace whenever the admin has an active payment
 * gate on this request (`request.payment_required`). Renders a fee CTA card that
 * opens a modal with the fee breakdown, optional reward-point redemption, and
 * the Razorpay checkout.
 *
 * Trigger vs. initiate: opening the modal does NOT create an order. The user
 * picks their points locally (a live preview), and only the "Pay Now" *trigger*
 * calls `POST /payments/checkout` — which atomically redeems the points AND
 * creates/refreshes the Razorpay order in one transaction. We then open
 * Razorpay's hosted checkout with the returned key/order and hand the signed
 * triple to `verify`. The frontend never touches card data; the backend is the
 * source of truth for amounts, the redemption cap and signature verification.
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { AlertCircle, CreditCard, Wallet, Check, Loader2 } from 'lucide-react';
import {
  Button,
  toast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
} from '@finmin/ui';
import { isApiError } from '@finmin/api-client';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import {
  isAdvanceDue,
  isMissingBillingDetailsError,
  useAdvance,
  useCheckout,
  useVerifyPayment,
  usePaymentStatus,
  type CheckoutResponse,
} from '@/api/payments';
import { useRewardWallet } from '@/api/rewards';
import { useMe } from '@/api/auth';
import { getServiceMeta } from '@/features/dashboard/constants';
import { useRazorpay, type RazorpaySuccess } from '@/features/payment/use-razorpay';
import { AdvancePayButton } from './advance-payment';

// ── Helpers ───────────────────────────────────────────────────────────────────

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

/** Format a rupee value (string like "2999.00" or number) as ₹2,999.00. */
function formatInr(value: string | number | null | undefined): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? inr.format(n) : '—';
}

/** Fee for an SR — max tier, falling back to min (matches backend logic). */
function feeFor(request: CustomerServiceRequestResponse): number {
  return Number(request.customer_service_fee_max ?? request.customer_service_fee_min ?? 0);
}

// ── CTA card + modal host ─────────────────────────────────────────────────────

interface Props {
  request: CustomerServiceRequestResponse;
}

export function PaymentSection({ request }: Props) {
  const [open, setOpen] = React.useState(false);
  const fee = feeFor(request);

  // Once captured, show a settled state with the breakdown instead of a pay CTA.
  // 404 (no order yet) is the normal pre-payment case here.
  const { data: status } = usePaymentStatus(request.id, { retry: false });
  const isPaid = status?.status === 'captured';
  // e.g. "₹2,000 fee · ₹500 via rewards · ₹270 GST · ₹500 advance paid earlier · ₹1,270 paid".
  const paidSummary = status
    ? [
        `${formatInr(status.amount)} fee`,
        Number(status.reward_discount) > 0 && `${formatInr(status.reward_discount)} via rewards`,
        Number(status.tax_amount) > 0 && `${formatInr(status.tax_amount)} GST`,
        Number(status.advance_paid) > 0 && `${formatInr(status.advance_paid)} advance paid earlier`,
        `${formatInr(status.net_amount)} paid`,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Payment received. Your provider is finalising your service.';

  return (
    <div className="mx-4 mt-4 rounded-2xl border border-border bg-background p-4 shadow-sm sm:mx-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
            {isPaid
              ? <Check className="h-5 w-5 text-primary" aria-hidden="true" />
              : <CreditCard className="h-5 w-5 text-primary" aria-hidden="true" />}
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">
              {isPaid ? 'Payment complete' : 'Your draft is ready'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isPaid ? paidSummary : `Service fee ${formatInr(fee)}. Pay to continue.`}
            </p>
          </div>
        </div>
        {!isPaid && (
          <Button type="button" size="sm" onClick={() => setOpen(true)} className="shrink-0">
            Pay now
          </Button>
        )}
      </div>

      <PaymentModal request={request} open={open} onOpenChange={setOpen} />
    </div>
  );
}

// ── Route-level blocking gate ───────────────────────────────────────────────

/**
 * PaymentGateOverlay — a blocking overlay shown the moment a customer opens a
 * service request that has an active admin payment gate (`payment_required`),
 * regardless of workflow stage.
 *
 * Admins can raise the gate at ANY point (processing, upload, workspace). The
 * inline <PaymentSection> only lives inside the workspace, so a customer gated
 * earlier would never see the prompt until they reached that screen. This
 * overlay surfaces the prompt immediately on entry with a stage-agnostic
 * message, and defers the fee/reward-point detail to the full <PaymentModal>
 * behind "Pay now". Renders nothing when no payment is due or once captured.
 */
export function PaymentGateOverlay({ request }: Props) {
  const [payOpen, setPayOpen] = React.useState(false);

  // 404 (no order yet) is the normal pre-payment case here — don't retry.
  const { data: status } = usePaymentStatus(request.id, { retry: false });
  const isPaid = status?.status === 'captured';
  const isPaymentDue = request.payment_required ?? false;

  // The final checkout is refused until a requested advance is paid, so offer
  // the advance first.
  const { data: advance, isPending: advancePending, isError: advanceError, refetch: refetchAdvance } =
    useAdvance(request.id, { enabled: isPaymentDue });
  const advanceDue = advance && isAdvanceDue(advance) ? advance : null;

  // Nothing to gate: no active gate, or already paid.
  if (!isPaymentDue || isPaid) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" aria-hidden="true" />

      {/* Hide the gate card while the checkout modal is open so we don't stack
          two cards; the backdrop stays to keep the page blocked. */}
      {!payOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-gate-title"
          aria-describedby="payment-gate-desc"
          className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-background p-6 text-center shadow-lg"
        >
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <CreditCard className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <h2 id="payment-gate-title" className="text-base font-semibold text-foreground">
            Payment required
          </h2>
          <p id="payment-gate-desc" className="mt-1.5 text-sm text-muted-foreground">
            {advanceDue
              ? `Please pay the advance of ${formatInr(advanceDue.amount)} first. It is deducted from your final bill.`
              : 'Please complete the payment to continue with this service.'}
          </p>
          <div className="mt-5 flex flex-col gap-2">
            {advanceDue ? (
              <AdvancePayButton request={request} advance={advanceDue} />
            ) : advanceError ? (
              // Unknown whether an advance is due — the final checkout would be
              // refused if it is, so retry instead of offering it.
              <Button type="button" variant="outline" onClick={() => void refetchAdvance()}>
                Retry
              </Button>
            ) : advancePending ? (
              <Button type="button" disabled>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                Loading…
              </Button>
            ) : (
              <Button type="button" onClick={() => setPayOpen(true)}>
                Pay now
              </Button>
            )}
            <Button asChild variant="ghost" size="sm">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </div>
        </div>
      )}

      <PaymentModal request={request} open={payOpen} onOpenChange={setPayOpen} />
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────────

interface ModalProps extends Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function PaymentModal({ request, open, onOpenChange }: ModalProps) {
  const serviceTitle = getServiceMeta(request.service_type).title;
  const fee = feeFor(request);

  const { openCheckout } = useRazorpay();
  // Prefill from /auth/me (unmasked) rather than the profile endpoint, whose
  // mobile/email are masked when CUSTOMER_RESPONSE_MASKING_ENABLED — a masked
  // contact breaks Razorpay's prefill and forces the contact-entry screen.
  const { data: me } = useMe();
  const { data: wallet } = useRewardWallet({ enabled: open });

  // An earlier order (if any) tells us whether GST is added on top; a paid
  // advance is deducted from the amount charged now.
  const { data: status } = usePaymentStatus(request.id, { retry: false, enabled: open });
  const { data: advance } = useAdvance(request.id, { enabled: open });

  const checkout = useCheckout();
  const verify   = useVerifyPayment();

  const [points, setPoints] = React.useState('');
  // The last /checkout response and the points it was made with. Its amounts
  // are authoritative (GST, advance, net) while the points are unchanged.
  const [quote, setQuote] = React.useState<{ points: number; order: CheckoutResponse } | null>(null);
  // Actionable 400 from /checkout (advance unpaid, billing state missing, ...).
  const [payError, setPayError] = React.useState<{ message: string; needsBillingDetails: boolean } | null>(null);
  // True from the checkout call through Razorpay to verification — blocks
  // closing / double-pay.
  const [checkoutBusy, setCheckoutBusy] = React.useState(false);
  const busy = checkoutBusy || verify.isPending;

  // Reset local state whenever the modal closes. No order is created on open —
  // it's created on the pay trigger via /checkout.
  React.useEffect(() => {
    if (!open) {
      setPoints('');
      setCheckoutBusy(false);
      setQuote(null);
      setPayError(null);
    }
  }, [open]);

  // Reward math (preview only — backend enforces the same caps in /checkout).
  // 1 point = ₹1; redemption capped at 50% of the fee AND the wallet balance.
  // Points held by this request's last /checkout are still spendable here — the
  // next /checkout releases them before redeeming again. Use that checkout's own
  // post-redemption balance so the held points aren't counted twice before the
  // wallet refetch lands.
  const balance = quote
    ? quote.order.remaining_reward_balance + quote.order.redeemed_points
    : (wallet?.available_balance ?? 0);
  const capByFee = Math.floor(fee * 0.5);
  const maxAddable = Math.min(balance, capByFee);
  const pointsNum = Math.max(0, Math.min(Number.parseInt(points || '0', 10) || 0, maxAddable));
  const canRedeem = maxAddable > 0;

  // Authoritative once /checkout has answered for these points; otherwise a
  // local preview. GST can't be previewed (the backend decides it), so when an
  // earlier order carried GST we only say it is added at checkout.
  const order = quote?.points === pointsNum ? quote.order : null;
  const shownFee = order ? Number(order.amount) : fee;
  const discount = order ? Number(order.reward_discount) : pointsNum; // 1 point = ₹1
  const taxAmount = order ? Number(order.tax_amount) : 0;
  const gstPending = !order && Number(status?.tax_amount ?? 0) > 0;
  const advancePaid = order
    ? Number(order.advance_paid)
    : advance?.status === 'captured' ? Number(advance.amount) : 0;
  const netAmount = order ? Number(order.net_amount) : Math.max(0, fee - discount - advancePaid);

  function handleVerify(resp: RazorpaySuccess) {
    verify.mutate(
      {
        serviceRequestId: request.id,
        payload: {
          razorpay_order_id:   resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature:  resp.razorpay_signature,
        },
      },
      {
        onSuccess: (data) => {
          setCheckoutBusy(false);
          if (data.status === 'captured') {
            toast.success('Payment successful!');
            onOpenChange(false);
          } else {
            toast.error('Payment could not be confirmed. If you were charged, it will reflect shortly.');
          }
        },
        onError: () => {
          setCheckoutBusy(false);
          toast.error('We could not verify your payment. If money was deducted, it will be reconciled shortly.');
        },
      },
    );
  }

  // The pay *trigger*: atomically redeem + create the order via /checkout, then
  // open Razorpay with the authoritative amount it returns.
  async function handlePay() {
    if (busy) return;
    setCheckoutBusy(true);
    setPayError(null);
    try {
      const order = await checkout.mutateAsync({
        service_request_id: request.id,
        points_to_redeem: pointsNum,
      });
      setQuote({ points: pointsNum, order });
      // Never charge more than the button showed: if /checkout added GST (or
      // otherwise changed the amount), show the real total and wait for a
      // second click.
      if (order.net_amount_paise !== Math.round(netAmount * 100)) {
        setCheckoutBusy(false);
        const gstAdded = Number(order.tax_amount) > 0 ? ' (GST added)' : '';
        toast.warning(`Total updated to ${formatInr(order.net_amount)}${gstAdded}. Review and pay.`);
        return;
      }
      await openCheckout({
        key:         order.razorpay_key_id,
        order_id:    order.razorpay_order_id,
        amount:      order.net_amount_paise,
        currency:    order.currency,
        name:        'FinminOS',
        description: serviceTitle,
        prefill: {
          name:    me?.name ?? undefined,
          email:   me?.email ?? undefined,
          contact: me?.mobile_e164 ?? undefined,
        },
        handler: handleVerify,
        modal: { ondismiss: () => setCheckoutBusy(false) },
      });
    } catch (err) {
      setCheckoutBusy(false);
      // /checkout surfaces actionable 400s (advance not paid yet, billing
      // state missing, no fee, over the redemption cap, etc.) — show the
      // backend's message in the modal so the customer can act on it.
      if (isApiError(err) && err.status === 400) {
        setPayError({ message: err.message, needsBillingDetails: isMissingBillingDetailsError(err) });
        return;
      }
      const message = isApiError(err)
        ? err.message
        : 'Could not start payment. Please try again.';
      toast.error(message);
    }
  }

  return (
    <Dialog
      open={open}
      // Non-modal: a *modal* Radix Dialog locks `pointer-events` on <body> and
      // traps focus, which makes Razorpay's hosted checkout (rendered on <body>)
      // unclickable. Non-modal keeps our dialog usable without stealing
      // interaction/focus from the Razorpay overlay.
      modal={false}
      onOpenChange={(next) => {
        if (busy) return; // don't let the user close mid-payment
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-md"
        // Interacting with the Razorpay overlay counts as "outside" this
        // content — don't let that close/steal focus from our dialog.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Pay for your {serviceTitle}</DialogTitle>
          <DialogDescription>
            Review your fee and complete payment securely via Razorpay.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Fee breakdown (live preview; /checkout returns the authoritative amount) */}
          <dl className="space-y-2 rounded-xl border border-border bg-muted/30 p-4 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">Service fee</dt>
              <dd className="font-medium text-foreground">{formatInr(shownFee)}</dd>
            </div>
            {discount > 0 && (
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">Reward discount</dt>
                <dd className="font-medium text-primary">−{formatInr(discount)}</dd>
              </div>
            )}
            {(taxAmount > 0 || gstPending) && (
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">GST</dt>
                <dd className="font-medium text-foreground">
                  {taxAmount > 0 ? `+${formatInr(taxAmount)}` : 'Added at checkout'}
                </dd>
              </div>
            )}
            {advancePaid > 0 && (
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">Less: advance paid</dt>
                <dd className="font-medium text-primary">−{formatInr(advancePaid)}</dd>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-border pt-2">
              <dt className="font-semibold text-foreground">
                {gstPending ? 'Amount payable (before GST)' : 'Amount payable'}
              </dt>
              <dd className="text-base font-bold text-foreground">{formatInr(netAmount)}</dd>
            </div>
          </dl>

          {payError && (
            <div role="alert" className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                <p>{payError.message}</p>
                {payError.needsBillingDetails && (
                  <Link to="/profile" search={{ tab: 'billing' }} className="font-semibold underline underline-offset-2">
                    Add billing details
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Reward redemption — selection is local; applied atomically on Pay */}
          {balance > 0 && (
            <div className="rounded-xl border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <Wallet className="h-4 w-4 text-primary" aria-hidden="true" />
                <span className="text-sm font-medium text-foreground">Reward points</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  Balance: {balance.toLocaleString('en-IN')}
                </span>
              </div>
              {canRedeem ? (
                <>
                  <p className="mb-2 text-xs text-muted-foreground">
                    1 point = ₹1. Use up to {maxAddable.toLocaleString('en-IN')} on this service (max 50% of the fee).
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={maxAddable}
                      value={points}
                      onChange={(e) => setPoints(e.target.value)}
                      placeholder="Points"
                      disabled={busy}
                      className="h-9"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPoints(String(maxAddable))}
                      disabled={busy}
                    >
                      Max
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Points can cover up to 50% of the fee.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handlePay} disabled={busy}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
            {busy ? 'Processing…' : gstPending ? 'Pay now' : `Pay ${formatInr(netAmount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
